import {
  CalendarDate,
  DateGranularity,
  DatePlaceholders,
  IsoDay,
  IsoMonth
} from '#UNSAFE_InputDateMask/types';

import {
  BCP47Locale,
  DateISOStr,
  DateTimeUtils,
  NativeDateTimeOptions,
  getFormatParse
} from '#UNSAFE_IntlDateTime';

const MAX_ISO_YEAR = 9999;
const MIN_ISO_YEAR = 0;
const MIN_ISO_DAY = 1;
const MIN_ISO_MONTH = 1;

const checkNoCalendarExtension = (locale: BCP47Locale) => {
  if (locale.indexOf('-u-ca-') !== -1) {
    throw new Error('Calendar not allowed in locale');
  }
};

const toJSDate = (year: number, month: IsoMonth, day: IsoDay) => {
  // Date uses 0-based index for month.
  return new Date(year, month - 1, day);
};

/**
 * This function formats a test date using Intl.DateTimeFormat for the given locale.
 * It then uses Intl.DateTimeFormat.formatToParts to get the parts of the date in a
 * locale specific order, substitutes translated placeholders, and filters out
 * unwanted parts of the date. If granularity = month, then the placeholder for day
 * is removed. The shape of the return value matches what is returned by formatToParts.
 *
 * @param locale The BCP47Locale
 * @param granularity Specifies whether you want placeholders for month, year OR day, month, year
 * @param monthPlaceholder The translated month placeholder
 * @param dayPlaceholder The translated day placeholder
 * @param yearPlaceholder The translated year placeholder
 */
const getDatePlaceholderMasks = (
  locale: BCP47Locale,
  granularity: 'month' | 'day',
  monthPlaceholder: string,
  dayPlaceholder: string,
  yearPlaceholder: string
): DatePlaceholders => {
  checkNoCalendarExtension(locale);
  const testDate = new Date('2022-11-30');
  const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short' });

  // Filter out anything that is not month, day, year, or literal (e.g. a separator).
  const filterByType = (p: Intl.DateTimeFormatPart) => {
    return (
      (granularity === 'day' && p.type === 'day') ||
      p.type === 'month' ||
      p.type === 'year' ||
      p.type === 'literal'
    );
  };

  // Substitute translated placeholders. Trim literals for visual consistency (they can include whitespace)
  // so the date looks more like a string when the placeholders are rendered next to each other.
  const mapTranslatedPlaceholders = (p: Intl.DateTimeFormatPart) => {
    switch (p.type) {
      case 'month':
        return { ...p, value: monthPlaceholder };
      case 'day':
        return { ...p, value: dayPlaceholder };
      case 'year':
        return { ...p, value: yearPlaceholder };
      default:
        return { ...p, value: p.value.trim() };
    }
  };

  const parts = formatter.formatToParts(testDate).filter(filterByType);
  let placeholders = parts.map(mapTranslatedPlaceholders);

  // Remove any repeated literals, which can occur after filtering (such as mm//yyyy).
  const index = placeholders.findIndex((element, index, array) => {
    return element.type === 'literal' && index > 0 && array[index - 1].type === 'literal';
  });
  if (index !== -1) {
    placeholders = placeholders.slice(0, index).concat(placeholders.slice(index + 1));
  }

  // Remove any leading or trailing literals. Leading literals can occur after filtering,
  // and certain locales include trailing literals such as mm.dd.yyyy. which we don't want.
  if (placeholders[placeholders.length - 1].type === 'literal') {
    placeholders = placeholders.slice(0, -1);
  }
  if (placeholders[0].type === 'literal') {
    placeholders = placeholders.slice(1);
  }

  return placeholders as DatePlaceholders;
};

/**
 * Return the number of days in a given month and year. Pass '*' if you don't know a value.
 * If neither month or year are known, then return the maximum number of days in any month in any year.
 * If only month is known, return the maximum number of days for that month in any year.
 * For Gregorian calendar:
 *   getDaysInMonth(2, *) => returns 29
 *   getDaysInMonth(*, *) => returns 31
 *   getDaysInMonth(*, 2023) => returns 31
 */
const getDaysInMonth = (month: IsoMonth | '*', year: number | '*') => {
  if (month === '*') {
    return 31;
  } else if (year === '*') {
    return getDaysInMonthGregorian(month);
  } else {
    checkIsValidIsoYear(year);
    // DateTimeUtils.getDaysInMonth uses a 0-based index.
    return DateTimeUtils.getDaysInMonth(year, month - 1);
  }
};

/**
 * Return the number of months in a given year. Pass '*' if you don't know the year.
 * In a Gregorian calendar the number of months is always 12. We'll have to revisit this
 * when multiple calendars are supported; in some cases the number of months can vary by year.
 */
const getMonthsInYear = (year: number | '*') => {
  if (year !== '*') {
    checkIsValidIsoYear(year);
  }
  return 12;
};

/**
 * Return the number of days in a month in the Gregorian calendar, without knowing the year.
 */
const getDaysInMonthGregorian = (month: IsoMonth) => {
  switch (month) {
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      return 31;
    case 2:
      return 29; // Since we don't know the year, return the maximum possible number of days.
    default:
      return 30;
  }
};

/**
 * Throw an error if year is not a valid ISO year (0-9999).
 * The allowed range is too large to express using Typescript.
 */
const checkIsValidIsoYear = (year: number) => {
  if (year < MIN_ISO_YEAR || year > MAX_ISO_YEAR) {
    throw new Error('Invalid year value');
  }
};

/**
 * This utility method uses Intl.DateTimeFormat directly to format with month: long and
 * year: numeric. It returns something like 'May 2025'.
 */
const getLongFormattedYearMonth = (locale: BCP47Locale, year: number, month: IsoMonth) => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  return formatter.format(toJSDate(year, month, 1));
};

/**
 * Construct and return an IS0 date string from year, month, and day.
 */
const getIsoDateStr = (year: number, month: IsoMonth, day: IsoDay) => {
  checkIsValidIsoYear(year);

  // Month and day must be 2 digits and year must be 4 digits to be a valid ISO 8601 string.
  const parts = [
    year.toString().padStart(4, '0'),
    month.toString().padStart(2, '0'),
    day.toString().padStart(2, '0')
  ];
  return parts.join('-');
};

/**
 * Parse a DateIsoStr and return an equivalent CalendarDate.
 */
const getCalendarDateFromIso = (str: DateISOStr) => {
  if (!DateTimeUtils.isDateOnlyIsoString(str)) {
    return undefined;
  }

  const parts = str.split('-');
  return { year: +parts[0], month: +parts[1], day: +parts[2] } as unknown as CalendarDate;
};

/**
 * Return the calendar date for year, month, and day formatted using the specified date style
 * in the given locale.
 */
const getFormattedYearMonthDay = (
  locale: BCP47Locale,
  year: number,
  month: IsoMonth,
  day: IsoDay,
  style: NonNullable<Intl.DateTimeFormatOptions['dateStyle']>
) => {
  checkNoCalendarExtension(locale);
  const dateIsoStr = getIsoDateStr(year, month, day);

  const dateFormatOptions: NativeDateTimeOptions = {
    locale,
    dateStyle: style,
    dateStyleShortYear: style === 'short' ? 'numeric' : undefined
  };
  const { format } = getFormatParse(dateFormatOptions);
  return format(dateIsoStr);
};

/**
 * Format and return the calendar date as a string. If the date is not a complete
 * date, i.e. any of its segments are missing, return an empty string.
 */
const formatCalendarDateAsString = (
  locale: BCP47Locale,
  calendarDate: CalendarDate,
  style: NonNullable<Intl.DateTimeFormatOptions['dateStyle']>
) => {
  if (!calendarDate.year || !calendarDate.month || !calendarDate.day) {
    return '';
  }
  return getFormattedYearMonthDay(
    locale,
    calendarDate.year,
    calendarDate.month,
    calendarDate.day,
    style
  );
};

/**
 * Format an example DateISOStr in short format.
 */
const formatIsoDateStrAsExample = (
  locale: BCP47Locale,
  str: DateISOStr,
  masks?: DatePlaceholders
) => {
  const calDate = getCalendarDateFromIso(str);
  // The placeholders aren't used for formatting, so use dummy values.
  const dateMasks = masks ?? getDatePlaceholderMasks(locale, 'day', 'mm', 'dd', 'yyyy');
  return calDate === undefined ? '' : formatShortCalendarDate(calDate, 'day', dateMasks);
};

/**
 * Format a CalendarDate in short format.
 */
const formatShortCalendarDate = (
  cd: CalendarDate,
  granularity: DateGranularity,
  masks: DatePlaceholders
) => {
  if (!cd.year || !cd.month || (granularity === 'day' && !cd.day)) {
    return '';
  }
  const dateValues = masks.map(({ type, value }) =>
    type === 'year' ? cd.year : type === 'month' ? cd.month : type === 'day' ? cd.day : value.trim()
  );
  return dateValues.join('');
};

/**
 * Format a CalendarDate in full format.
 */
const formatFullCalendarDate = (
  locale: BCP47Locale,
  cd: CalendarDate,
  granularity: DateGranularity
) => {
  if (!cd.year || !cd.month || (granularity === 'day' && !cd.day)) {
    return '';
  }
  checkNoCalendarExtension(locale);

  return granularity === 'day'
    ? getFormattedYearMonthDay(locale, cd.year, cd.month, cd.day!, 'full')
    : getLongFormattedYearMonth(locale, cd.year, cd.month);
};

// returns true if undefined or if the value has a year, month and day property that are valid based on granularity.
// The day property can be undefined if granularity is month.
const isValidCalendarDate = (value: any, granularity: DateGranularity = 'day') => {
  if (value === undefined) return true;
  if (value === null) return false;
  // if not undefined, then it must be all empty or a complete date based on granularity
  const { year, month, day } = value;

  const isValidNumber = (num: number | undefined, max: number | undefined): boolean => {
    return (
      typeof num === 'number' &&
      Number.isInteger(num) &&
      num > 0 &&
      (max === undefined || num <= max)
    );
  };

  // Empty object is not ok.
  if (year === undefined && month === undefined && (granularity === 'month' || day === undefined)) {
    return false;
  }

  // Check if the value is a complete object and its month and day dates are valid
  if (month !== undefined && year !== undefined) {
    if (
      granularity === 'day' &&
      isValidNumber(year, MAX_ISO_YEAR) &&
      isValidNumber(month, getMonthsInYear(year)) &&
      isValidNumber(day, getDaysInMonth(month, year))
    ) {
      return true;
    } else if (
      granularity === 'month' &&
      isValidNumber(year, MAX_ISO_YEAR) &&
      isValidNumber(month, getMonthsInYear(year))
    ) {
      return true;
    }
  }
  return false;
};

const isCompleteCalendarDate = (value: CalendarDate) => {
  const { year, month, day } = value;

  // Check if the value is a complete object and its month and day dates are valid
  return year !== undefined && month !== undefined && day !== undefined;
};

// Given a pattern, return a DatePlaceholder array.
const getDatePlaceholdersFromPattern = (pattern: string) => {
  const arr = getMaskArrayFromPattern(pattern);
  const datePlaceholders = convertMaskArrayToDatePlaceholders(arr);
  return datePlaceholders;
};

// Given a pattern, like 'd-m-yyyy', return an array like ['d', '-', 'm', '-', 'yyyy']
const getMaskArrayFromPattern = (pattern: string) => {
  const result: Array<string> = [];
  let currentSegmentStr = '';
  let currentLiteral = '';

  const addSegment = () => {
    if (currentSegmentStr !== '') {
      result.push(currentSegmentStr);
      currentSegmentStr = '';
    }
  };

  const addLiteral = () => {
    if (currentLiteral !== '') {
      // InputDateMask does not want spaces in its literals. Even if InputDateMask
      // trims its literals for the masks property, we do so here as well
      // since we could end up using this for things that are
      // outside of InputDateMasks masks property.
      result.push(currentLiteral.trim());
      currentLiteral = '';
    }
  };

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    if (char.toLowerCase() === 'm' || char.toLowerCase() === 'd' || char.toLowerCase() === 'y') {
      addLiteral();
      currentSegmentStr += char;
    } else {
      addSegment();
      currentLiteral += char;
    }
  }

  // Add the last components
  addSegment();
  addLiteral();

  return result;
};

// converts an array into the object that is needed to set on the InputDateMask's masks property, DatePlaceholder[];
const convertMaskArrayToDatePlaceholders = (array: Array<string>) => {
  return array.map((item) => {
    if (item.toLowerCase()[0] === 'd') {
      return { type: 'day', value: item };
    } else if (item.toLowerCase()[0] === 'm') {
      return { type: 'month', value: item };
    } else if (item.toLowerCase()[0] === 'y') {
      return { type: 'year', value: item };
    } else {
      // Default to literal for anything else
      return { type: 'literal', value: item };
    }
  }) as DatePlaceholders;
};

export {
  MIN_ISO_DAY,
  MIN_ISO_MONTH,
  MIN_ISO_YEAR,
  MAX_ISO_YEAR,
  checkIsValidIsoYear,
  checkNoCalendarExtension,
  formatCalendarDateAsString,
  formatShortCalendarDate,
  formatFullCalendarDate,
  formatIsoDateStrAsExample,
  getCalendarDateFromIso,
  getDatePlaceholderMasks,
  getFormattedYearMonthDay,
  getDaysInMonth,
  getDatePlaceholdersFromPattern,
  getIsoDateStr,
  getMonthsInYear,
  isCompleteCalendarDate,
  isValidCalendarDate
};
